import { decodeRedirectUrl } from "~/utils/sanitize-redirect-url";
import { LoginPropsCommon } from "./typings";
import { Trans } from "@lingui/react/macro";
import { EyeIcon, EyeSlashIcon, XCircleIcon } from "@heroicons/react/24/outline";
import React, { FormEvent, useEffect, useState } from "react";
import { isAndroid, isPWA } from "~/utils/device-check";
import { AppleIcon, GoogleIcon, LogoPurpleMark, MicrosoftIcon } from "../icons";
import { useSearchParams } from "react-router";
import { classNames } from "~/utils";
import LoginEmail from "./login-email";

interface LoginProps extends LoginPropsCommon {
  handleGoogle: () => void;
  handleApple: () => void;
  handleLinkedin?: () => void;
  /** Absent when Microsoft sign-in is not configured for this environment (no button). */
  handleMicrosoft?: () => void;
  submitForgot: (e: FormEvent, email: string) => Promise<boolean>;
  resendPIN: (e: FormEvent, email: string) => Promise<boolean>;
  submitReset: (e: FormEvent, email: string, pin: string, password: string) => Promise<boolean>;
  validatePin: (e: FormEvent, email: string, pin: string) => Promise<boolean>;
  reset: boolean;
  success: boolean;
  error: boolean;
}

export function Login({
  handleGoogle,
  handleApple,
  handleLinkedin,
  handleMicrosoft,
  reset,
  appRoutes,
  redirectUrl,
  routerUtils,
  success,
  error,
  submitForgot,
  resendPIN,
  submitReset,
  validatePin,
}: LoginProps) {
  const { useLocation, Link } = routerUtils;
  const [hideAppleSignIn, setHideAppleSignIn] = useState(false);
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  searchParams.delete("error");

  useEffect(() => {
    if (isPWA() && isAndroid()) {
      setHideAppleSignIn(true);
    }
  }, []);

  useEffect(() => {
    if (error) {
      setPassword("");
      setSubmitting(false);
    }
  }, [error]);

  let actualRedirectUrl = "";
  if (searchParams.get("redirectUrl")) {
    actualRedirectUrl = decodeRedirectUrl(searchParams.get("redirectUrl") || "/");
  }

  const inviteCode = searchParams.get("inviteCode") || "";

  return (
    <div className="flex flex-col justify-center items-center w-full max-w-[400px] flex-1 gap-10">
      {/* Header */}
      <div className="text-center flex flex-col gap-2">
        {/* Mobile-only small brand mark */}
        <div className="lg:hidden flex justify-center mb-3">
          <LogoPurpleMark width={40} height={40} />
        </div>
        <h2 className="text-[30px] font-semibold leading-[36px] tracking-[-0.225px] text-foreground"><Trans>Welcome back</Trans></h2>
        <p className="text-base leading-7 text-muted-foreground"><Trans>We're excited to see you again!</Trans></p>
      </div>

      {/* Main Actions */}
      <div className="flex flex-col w-full">
        {/* OAuth Buttons and OR Separator */}
        <div className="flex flex-col gap-6 w-full">
          {/* OAuth Buttons - Horizontal Row */}
          <div className="flex gap-3 w-full">
            <button
              type="button"
              className="flex-1 h-12 px-3 flex items-center justify-center gap-2 bg-background border border-border rounded-lg hover:bg-accent transition-colors"
              onClick={handleGoogle}
            >
              <GoogleIcon aria-hidden="true" className="w-5 h-5" />
              <span className="text-base font-semibold leading-6 text-foreground"><Trans>Google</Trans></span>
            </button>

            {!hideAppleSignIn && (
              <button
                type="button"
                className="flex-1 h-12 px-3 flex items-center justify-center gap-2 bg-background border border-border rounded-lg hover:bg-accent transition-colors"
                onClick={handleApple}
              >
                <AppleIcon aria-hidden="true" className="w-5 h-5" />
                <span className="text-base font-semibold leading-6 text-foreground"><Trans>Apple</Trans></span>
              </button>
            )}

            {handleMicrosoft && (
              <button
                type="button"
                data-testid="signin-microsoft"
                className="flex-1 h-12 px-3 flex items-center justify-center gap-2 bg-background border border-border rounded-lg hover:bg-accent transition-colors"
                onClick={handleMicrosoft}
              >
                <MicrosoftIcon aria-hidden="true" className="w-5 h-5" />
                <span className="text-base font-semibold leading-6 text-foreground"><Trans>Microsoft</Trans></span>
              </button>
            )}

            <Link
              className="flex-1"
              to={{
                pathname: appRoutes.LoginSAML,
                search: searchParams.toString(),
              }}
            >
              <button
                type="button"
                className="w-full h-12 px-3 flex items-center justify-center gap-2 bg-background border border-border rounded-lg hover:bg-accent transition-colors"
              >
                <span className="text-base font-semibold leading-6 text-foreground">SSO</span>
              </button>
            </Link>
          </div>

          {/* OR Separator */}
          <div className="flex items-center gap-3 w-full">
            <div className="flex-1 h-px bg-border"></div>
            <span className="text-xs leading-5 text-placeholder-foreground">OR</span>
            <div className="flex-1 h-px bg-border"></div>
          </div>
        </div>

        {/* Form - with custom top margin */}
        <div className="mt-4">
          <LoginEmail
            routerUtils={{ Link, useLocation }}
            appRoutes={appRoutes}
            redirectUrl={actualRedirectUrl || ""}
            success={success}
            error={error}
            submitForgot={submitForgot}
            resendPIN={resendPIN}
            submitReset={submitReset}
            validatePin={validatePin}
          />
        </div>
      </div>
    </div>
  );
}

export default Login;
